import { Sparkles } from 'lucide-react'
import { Button } from '../ui'
import type { FloorPlanOptimizationResult } from '../../lib/floorPlan/rfOptimizer'

type Props = {
  result: FloorPlanOptimizationResult
  onConfirm: (includeNewAps: boolean) => void
  onCancel: () => void
  applying: boolean
}

export default function FloorPlanOptimizeConfirm({ result, onConfirm, onCancel, applying }: Props) {
  const { newAps, changes, summary } = result

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        className="w-full max-w-md rounded-xl border border-mg-border bg-mg-surface p-4 shadow-xl"
        role="dialog"
        aria-labelledby="optimize-confirm-title"
      >
        <div className="flex items-start gap-2">
          <Sparkles size={18} className="mt-0.5 shrink-0 text-mg-accent" />
          <div className="min-w-0 flex-1">
            <h2 id="optimize-confirm-title" className="text-sm font-semibold text-mg-text">
              Apply optimization?
            </h2>
            <p className="mt-1 text-xs text-mg-muted">
              Review changes before saving. Adding APs uses inventory devices and counts toward your deployed count.
            </p>
          </div>
        </div>

        <ul className="mt-3 max-h-36 space-y-1 overflow-y-auto text-[11px] text-mg-text">
          {summary.map(line => (
            <li key={line} className="flex gap-1.5">
              <span className="text-mg-accent">•</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        {newAps.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200/80 bg-amber-50/60 p-2.5 dark:border-amber-900 dark:bg-amber-950/30">
            <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
              {newAps.length} new AP{newAps.length > 1 ? 's' : ''} proposed
            </p>
            <ul className="mt-1 space-y-0.5 text-[11px] text-amber-800 dark:text-amber-200">
              {newAps.map(ap => (
                <li key={`${ap.apName}-${ap.x}-${ap.y}`}>
                  {ap.apName} at ({Math.round(ap.x)}%, {Math.round(ap.y)}%) · {ap.txPowerDbm ?? 15} dBm
                </li>
              ))}
            </ul>
          </div>
        )}

        {changes.length > 0 && newAps.length === 0 && (
          <p className="mt-2 text-[11px] text-mg-muted">
            {changes.length} existing AP adjustment{changes.length > 1 ? 's' : ''} (power, channel, or position).
          </p>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button
            variant="primary"
            size="sm"
            type="button"
            className="flex-1"
            disabled={applying}
            onClick={() => onConfirm(true)}
          >
            {applying ? 'Applying…' : newAps.length > 0 ? 'Apply all changes' : 'Apply changes'}
          </Button>
          {newAps.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              className="flex-1"
              disabled={applying}
              onClick={() => onConfirm(false)}
            >
              Apply without new APs
            </Button>
          )}
          <Button variant="ghost" size="sm" type="button" disabled={applying} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}
